import React, { useCallback, useState } from "react";
import { ModalCloseIcon } from "../ModalCloseIcon";
import StripePayBtn from "./StripePayBtn";
import SaleCountdown from "./SaleCountdown";
import ShopFooter from "./ShopFooter";
import { VIRTUAL_CURRENCY_SHOP_NOTE } from "../../utils/virtualCurrencyDisclaimer";

interface PaymentModalProps {
  receiveAmount: number;
  payAmount: number;
  /** Full price when a sale is active; shown struck through next to `payAmount`. */
  originalPayAmount?: number;
  saleEndsAt?: string | null;
  closeModal: (success?: boolean) => void;
  offerId: number;
  updateRubies: (rubies: number) => void;
  offerType: string;
  setIsAnimatingRubies?: (isAnimating: boolean) => void;
}

export default function PaymentModal({
  receiveAmount,
  payAmount: listedPayAmount,
  originalPayAmount,
  saleEndsAt,
  closeModal,
  offerId,
  updateRubies,
  offerType,
  setIsAnimatingRubies,
}: PaymentModalProps) {
    const [isSuccess, setIsSuccess] = useState(false);
    const [payAmount, setPayAmount] = useState(listedPayAmount);

    const wrappedCloseModal = useCallback(
      (success?: boolean) => {
        closeModal(success);
      },
      [closeModal]
    );

    function handlePaymentSuccess() {
        setIsSuccess(true);
    }

    function handleDone() {
        if (setIsAnimatingRubies) {
            setIsAnimatingRubies(true);
        }
        wrappedCloseModal(true);
        if (setIsAnimatingRubies) {
            setTimeout(() => {
                setIsAnimatingRubies(false);
            }, 3000);
        }
    }

    // Success view
    if (isSuccess) {
        return (
            <div className="w-[100%]">
                {/* Success Header */}
                <div 
                    className="w-[100%] py-[20px] flex flex-row justify-between items-center relative"
                    style={{ backgroundColor: '#401A6E' }}
                >
                    <div className="flex-1"></div>
                    <p className="font-anton text-white text-[24px] flex-1 text-center">SUCCESS!</p>
                    <div className="flex-1 flex justify-end pr-[7px]">
                        <button
                            type="button"
                            onClick={() => wrappedCloseModal(false)}
                            className="text-white hover:text-gray-300 transition-colors flex items-center justify-center"
                            aria-label="Close"
                            style={{
                                height: "40px",
                                width: "40px",
                            }}
                        >
                            <ModalCloseIcon width={24} height={24} fill="currentColor" className="shrink-0" />
                        </button>
                    </div>
                </div>

                {/* White Content Panel */}
                <div 
                    className="w-[100%] bg-white px-[20px] py-[10px] flex flex-col items-center justify-center"
                    style={{ backgroundColor: '#FFFFFF' }}
                >
                    {/* Main Message */}
                    <p className="font-anton text-main text-[20px]">
                        YOUR WALLET HAS BEEN CREDITED
                    </p>

                    {/* Ruby Icon */}
                    <img 
                        className="w-[90px] h-[90px]" 
                        src="/assets/currency-ruby.png" 
                        alt="ruby"
                    />

                    {/* Amount Added */}
                    <p className="font-anton text-main text-[32px]">
                        x{receiveAmount}
                    </p>
                    {originalPayAmount != null && originalPayAmount > payAmount ? (
                        <p className="mt-[2px] bg-red px-[10px] py-[2px] font-anton text-[16px] uppercase text-white">
                            You saved ₹{originalPayAmount - payAmount}!
                        </p>
                    ) : null}
                </div>

                <div className="w-[100%] flex flex-row justify-center items-center py-[20px]">
                    {/* DONE Button */}
                    <button
                        onClick={handleDone}
                        style={{
                            width: "290px",
                            height: "50px",
                            backgroundColor: "#D2FF00",
                            color: "#1B062F",
                            fontWeight: "400",
                            fontFamily: "Anton",
                            fontSize: "16px",
                            lineHeight: "24px",
                            textAlign: "center",
                        }}
                    >
                        DONE
                    </button>
                </div>

                <ShopFooter />
            </div>
        );
    }

    // Purchase view (original)
    return (
        <>
            <div 
                className="flex flex-row justify-between items-center h-[70px] px-[7px] relative"
                style={{
                    background: 'linear-gradient(88.89deg, #652DAF 0%, #623998 100%)'
                }}
            >
                <div className="flex flex-row items-center gap-1">
                    <img className="w-[40px] h-[40px] mx-2" src="/assets/currency-ruby.png"/>
                    <p className="font-anton text-white text-[20px]">
                        GET x{receiveAmount} RUBIES
                    </p>
                </div>
                <div className="flex flex-row items-center gap-[8px]">
                    {originalPayAmount != null && originalPayAmount > payAmount ? (
                        <p className="font-anton text-[14px] text-white/60 line-through" aria-label={`Was ₹${originalPayAmount}`}>
                            ₹{originalPayAmount}
                        </p>
                    ) : null}
                    <p
                        className={`font-anton text-main bg-lime py-[8px] ${
                            originalPayAmount != null && originalPayAmount > payAmount ? "px-[16px]" : "px-[30px]"
                        }`}
                    >
                        ₹{payAmount}
                    </p>
                    <button
                        type="button"
                        onClick={() => wrappedCloseModal(false)}
                        className="text-white hover:text-gray-300 transition-colors flex items-center justify-center"
                        style={{
                            height: "40px",
                            width: "40px",
                        }}
                        aria-label="Close"
                    >
                        <ModalCloseIcon width={24} height={24} fill="currentColor" className="shrink-0" />
                    </button>
                </div>
            </div>
            {originalPayAmount != null && originalPayAmount > payAmount ? (
                <div className="flex flex-row flex-wrap items-center justify-center gap-x-2 bg-red px-4 py-1 text-white">
                    <span className="font-anton text-[12px] uppercase">
                        Sale: save ₹{originalPayAmount - payAmount}
                    </span>
                    {saleEndsAt ? <SaleCountdown endsAt={saleEndsAt} className="text-white/90" /> : null}
                </div>
            ) : null}
            <p className="bg-[#4E2681] px-4 py-1.5 text-center font-chakra text-[11px] leading-snug text-white/70 normal-case">
                {VIRTUAL_CURRENCY_SHOP_NOTE}
            </p>
            <div className="bg-lime h-[30px] flex flex-row justify-start items-center">
                <p className="text-main font-anton ml-[15px]">PAYMENT METHOD</p>
            </div>
            <div className="flex flex-row items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex flex-row items-center gap-2 min-w-0">
                    <span
                        className="inline-flex items-center justify-center w-7 h-7 bg-[#1B062F] text-white shrink-0"
                        aria-hidden
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M7 10V8a5 5 0 1 1 10 0v2"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
                        </svg>
                    </span>
                    <span className="font-semibold text-main text-[13px] leading-tight">
                        <span className="block">Guaranteed and</span>
                        <span className="block">secured payments</span>
                    </span>
                </div>
                <span className="inline-flex items-center bg-[#1B062F] px-2 py-2 shrink-0">
                    <span className="text-white text-[8px] font-semibold uppercase tracking-[0.04em]">
                        Powered by
                    </span>
                    <img
                        src="/assets/stripe.svg"
                        alt="Stripe"
                        className="h-[28px] w-auto object-contain brightness-0 invert"
                    />
                </span>
            </div>
            <div
                className="flex flex-col items-center gap-2 p-2 pb-8 overflow-y-auto overscroll-contain hide-scrollbar"
                style={{
                    minHeight: "min(60vh, 400px)",
                    maxHeight: "min(60vh, 400px)",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                <StripePayBtn
                    closeModal={wrappedCloseModal}
                    offerId={offerId}
                    payAmount={payAmount}
                    updateRubies={updateRubies}
                    offerType={offerType}
                    onPaymentSuccess={handlePaymentSuccess}
                    onPriceChange={setPayAmount}
                />
            </div>

            <ShopFooter className="my-[18px] flex-shrink-0" />
        </>
    );
}
